<script>
import {InspectorsService} from "@/services/InspectorsService";
import Swal from "sweetalert2";
import {InspectorsCompaniesService} from "@/services/InspectorsCompaniesService";
import RsInspectorYearChart from "@/components/dashboard/rightship/inspectors/RsInspectorYearChart.vue";
import {ExcelService} from "@/services/ExcelService";
import {permissionMixin} from "@/mixins/permissionMixins";

export default {
  name: "RsInspectorDashboard",
  mixins : [permissionMixin],
  components: {RsInspectorYearChart},

  data(){
    return{
      inspectors: [],
      companies: [],
      rightship_data: [],
      rightship_chart_data: [],
      loading: false,
      expandedRows: {},
      selectedCompany: null,
      selectedInspector: null,
      inspector_first_name: null,
      inspector_last_name: null,
      inspector_company: null,
      filters: {
        company_id: "",
        inspector_id: "",
      }
    };
  },
  methods:{
    showAlert(icon, title, text = "", showCancel = false) {
      return Swal.fire({
        icon,
        title,
        text,
        showConfirmButton: true,
        confirmButtonColor: "#42b983",
        showCancelButton: showCancel,
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: true,
        confirmButtonText: "OK",
      });
    },

    showLoadingAlert() {
      Swal.fire({
        title: "Loading...",
        text: "Please wait.",
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
    },

    closeLoadingAlert() {
      Swal.close();
    },

    toggleExpand(key, field) {
      if (!this.expandedRows[key]) {
        this.$set(this.expandedRows, key, { corrective: false, preventative: false });
      }
      this.expandedRows[key][field] = !this.expandedRows[key][field];
    },
    isExpanded(key, field) {
      return this.expandedRows[key] && this.expandedRows[key][field];
    },
    truncateHtml(html, length = 200) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = div.textContent || div.innerText || "";
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    contentLength(html) {
      if (!html) return 0;
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = div.textContent || div.innerText || "";
      return text.length;
    },

    async fetchInspectorsCompanies() {
      try {
        const response = await InspectorsCompaniesService.getAll();
        this.companies = response.data;
      } catch (error) {
        console.error("Failed to load companies", error);
        this.showToast("error", "Failed to load companies");
      }
    },

    async fetchInspectors(id) {
      this.showLoadingAlert();
      try {
        const response = await InspectorsService.showCompanyInspectors(id);
        this.inspectors = response.data;
      } catch (error) {
        console.error("Failed to load inspectors", error);
        this.showAlert("error", "Failed to load inspectors!");
      } finally {
        this.closeLoadingAlert();
      }
    },

    async getCompanyId(){
      this.selectedInspector = null;
      this.inspector_first_name = null;
      this.inspector_last_name = null;
      this.fetchInspectors(this.selectedCompany);
      this.inspector_company = this.companies.find(c => c.id === this.selectedCompany)?.name || null;
      this.rightship_chart_data = [];
      this.rightship_data = [];
    },

    async getInspectorDetails(){
      this.inspector_first_name = this.inspectors.find(c => c.id === this.selectedInspector)?.first_name || null;
      this.inspector_last_name = this.inspectors.find(c => c.id === this.selectedInspector)?.last_name || null;
      this.rightship_chart_data = [];
      this.getInspectorRightshipData();
    },

    async getInspectorRightshipData(){
      this.showLoadingAlert();
      this.filters.inspector_id = this.selectedInspector;
      this.filters.company_id = this.selectedCompany;
      var response = await InspectorsService.showRightshipReport(this.filters);
      if(response){
        this.rightship_data = response.data.data;
        this.closeLoadingAlert();
      }
    },

    async getRightshipChartData(){
      this.showLoadingAlert();
      const response = await InspectorsService.getRsInspectorCharts(this.filters);
      if(response){
        if(this.rightship_data.length){
          this.rightship_chart_data = response.data;
          this.closeLoadingAlert();
          setTimeout(() => {
            this.$nextTick(() => {
              if (this.$refs.graphSection) {
                this.$refs.graphSection.scrollIntoView({
                  behavior: "smooth",
                  block: "start"
                });
              }
            });
          }, 600);
        }
        else{
          this.showAlert('info','Empty Results', 'No results found to generate graph.')
        }
      }
    },

    async generateCsv() {
      swal.fire({
        title: "GENERATING CSV FILE",
        allowEscapeKey: false,
        allowEnterKey: false,
        allowOutsideClick: false
      });
      swal.showLoading();
      await ExcelService.csv("RIGHTSHIP_EVENT", {
        inspector_id: this.selectedInspector,
      });
      swal.close();
    },

    async generateInspectorCsv() {
      swal.fire({
        title: "GENERATING CSV FILE",
        allowEscapeKey: false,
        allowEnterKey: false,
        allowOutsideClick: false
      });
      swal.showLoading();
      await ExcelService.csv("RIGHTSHIP_INSPECTOR_EVENT", {
        inspector_id: this.selectedInspector,
      });
      swal.close();
    },
  },
  computed:{

  },
  watch:{

  },
  async created(){
    this.fetchInspectorsCompanies();

  }
}
</script>

<template>
<div class="container-fluid">
  <div class="row mt-4">
    <div class="col-lg-12 col-md-12 col-sm-12">
      <h5 class="font-weight-bold text-uppercase" style="color: black;">Findings by Inspector</h5>
    </div>
  </div>
  <hr style="border: #d7d7d7 2px solid">
  <div class="row mb-3">
    <div class="col-12">
      <div class="card border border-dark">
        <div class="card-header">
          <div class="row">
            <div class="col-6 h5 text-uppercase font-weight-bold d-flex align-items-center mt-2" style="color: black">
              Rightship Report Details
            </div>
            <div class="col-6 d-flex align-items-center justify-content-end">
              <button
                class="btn btn-sm text-uppercase font-weight-bold"
                @click="getRightshipChartData"
                style="font-size: 9pt;"
                :disabled="!selectedCompany || !selectedInspector"
                :class="{
                  'btn-success': selectedCompany && selectedInspector,
                  'btn-dark btn-disabled cursor-not-allowed': !selectedCompany || !selectedInspector
                }"
              >
                Generate Graph
              </button>
              <button
                v-if="['k-line-persons', 'k-line-persons2'].includes(user.roles_permissions?.roles[0]) === false"
                class="btn btn-sm text-uppercase font-weight-bold ml-2"
                @click="generateInspectorCsv"
                style="font-size: 9pt;"
                :disabled="!rightship_data.length"
                :class="{
                  'btn-primary': rightship_data.length,
                  'btn-dark btn-disabled cursor-not-allowed': !rightship_data.length
                }"
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div>
            <table class="table table-bordered" style="border: 2px solid black !important;">
              <thead>
              <tr>
                <td class="align-middle text-right bg-warning font-weight-bold text-uppercase">Company Name</td>
                <td colspan="7" class="text-uppercase">
                  <select class="form-control form-control-sm text-uppercase" v-model="selectedCompany" @change="getCompanyId()">
                    <option disabled :value="null">-- Select Company --</option>
                    <template v-for="(data, index) in companies">
                      <option :value="data.id">{{data.name}}</option>
                    </template>
                  </select>
                </td>
              </tr>

              <tr>
                <td class="align-middle text-right bg-warning font-weight-bold text-uppercase" style="width: 15%;">Name of Inspector</td>
                <td colspan="7" class="text-uppercase">
                  <select class="form-control form-control-sm text-uppercase" v-model="selectedInspector" @change="getInspectorDetails()" :disabled="!selectedCompany">
                    <option disabled :value="null">-- Select Inspector --</option>
                    <template v-for="(data, index) in inspectors">
                      <option :value="data.id">{{data.last_name}}, {{data.first_name}}</option>
                    </template>
                  </select>
                </td>
              </tr>

              </thead>
            </table>
          </div>
          <div class="table-responsive" style="overflow-x: auto; -webkit-overflow-scrolling: touch; max-height: 600px;">
            <table class="table table-bordered" style="border: 2px solid black !important; min-width: 2000px;">
              <thead>
              <tr>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 2% !important; background-color: #e1e1e1;">#</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 8% !important; background-color: #e1e1e1;">Rightship Report No.</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 8% !important; background-color: #e1e1e1;">Vessel Name</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 2% !important; background-color: #e1e1e1;">Section / Code</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 20% !important; background-color: #e1e1e1;">List of Findings</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 20% !important; background-color: #e1e1e1;">Deficiency</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 20% !important; background-color: #e1e1e1;">Corrective Action</th>
                <th class="text-uppercase align-middle text-center font-weight-bold sticky-header" style="width: 20% !important; background-color: #e1e1e1;">Preventive Measures</th>
              </tr>
              </thead>
              <tbody v-if="loading">
              <tr>
                <td colspan="8">
                  <div class="card" style="background-color: #e1e1e1">
                    <div class="card-body text-uppercase text-center font-weight-bold h5">
                      Loading...
                    </div>
                  </div>
                </td>
              </tr>
              </tbody>
              <tbody v-else-if="!loading && rightship_data.length === 0">
                <tr>
                  <td colspan="8">
                    <div class="card" style="background-color: #e1e1e1">
                        <div class="card-body text-uppercase text-center font-weight-bold h5">
                          No data found
                        </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
              <template v-for="(row, rowIndex) in rightship_data">
                <tr v-for="(data, index2) in row.non_conformities" :key="rowIndex + '-' + index2">
                  <td v-if="index2 === 0" :rowspan="row.non_conformities.length" class="align-middle text-center">
                    {{rowIndex+1}}
                  </td>
                  <td v-if="index2 === 0" :rowspan="row.non_conformities.length" class="align-middle text-center">
                    {{ row?.rs_code }}
                  </td>
                  <td v-if="index2 === 0" :rowspan="row.non_conformities.length" class="align-middle text-center">
                    {{ row?.vessel_name }}
                  </td>
                  <td class="align-middle text-center h6">{{ data?.rsn_question?.code }}</td>
                  <td>{{ data?.rsn_question?.main_question }}</td>
                  <td>
                    <span v-html="data?.description"></span>
                  </td>
                  <td v-if="data?.corrective_action?.instruction">
                    <span
                      v-if="!isExpanded(rowIndex + '-' + index2, 'corrective') && contentLength(data?.corrective_action?.instruction) > 200"
                      v-html="truncateHtml(data?.corrective_action?.instruction, 200)">
                    </span>
                    <span
                      v-else
                      v-html="data?.corrective_action?.instruction">
                    </span>
                    <a
                      v-if="contentLength(data?.corrective_action?.instruction) > 200"
                      href="javascript:void(0)"
                      class="text-blue-500 ml-2"
                      @click="toggleExpand(rowIndex + '-' + index2, 'corrective')"
                    >
                      {{ isExpanded(rowIndex + '-' + index2, 'corrective') ? "See Less" : "See More" }}
                    </a>
                  </td>
                  <td v-else> - </td>
                  <td v-if="data?.preventative_measure?.description">
                    <span
                      v-if="!isExpanded(rowIndex + '-' + index2, 'preventative') && contentLength(data?.preventative_measure?.description) > 200"
                      v-html="truncateHtml(data?.preventative_measure?.description, 200)">
                    </span>
                    <span
                      v-else
                      v-html="data?.preventative_measure?.description">
                    </span>
                    <a
                      v-if="contentLength(data?.preventative_measure?.description) > 200"
                      href="javascript:void(0)"
                      class="text-blue-500 ml-2"
                      @click="toggleExpand(rowIndex + '-' + index2, 'preventative')"
                    >
                      {{ isExpanded(rowIndex + '-' + index2, 'preventative') ? "See Less" : "See More" }}
                    </a>
                  </td>
                  <td v-else> - </td>
                </tr>
              </template>
              </tbody>

            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="row mb-3" v-if="rightship_chart_data.length" ref="graphSection">
    <div class="col-12">
      <div class="card border border-dark" >
        <div class="card-header">
          <div class="row">
            <div class="col-6 h5 text-uppercase font-weight-bold d-flex align-items-center mt-2" style="color: black">
              Graph
            </div>
            <div class="col-6 d-flex align-items-center justify-content-end">
              <button
                v-if="['k-line-persons', 'k-line-persons2'].includes(user.roles_permissions?.roles[0]) === false"
                class="btn btn-sm text-uppercase font-weight-bold"
                @click="generateCsv()"
                style="font-size: 9pt;"
                :disabled="!selectedCompany || !selectedInspector"
                :class="{
                  'btn-primary': selectedCompany && selectedInspector,
                  'btn-dark btn-disabled cursor-not-allowed': !selectedCompany || !selectedInspector
                }"
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div>
            <RsInspectorYearChart :raw-data="rightship_chart_data" :inspector="selectedInspector"></RsInspectorYearChart>
          </div>

        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
  border-top: 2px solid black;
  border-bottom: 2px solid black;
}

.table-bordered {
  border-collapse: separate !important;
  border-spacing: 0;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid black !important;
}

.cursor-not-allowed {
  cursor: not-allowed !important;
}

</style>
